import { Theme, ThemeMode, ThemeColorPalettes } from '../types/theme';

// Base colors
const baseColors = {
  white: '#FFFFFF',
  black: '#000000',
};

// Gray Light Palette
const grayLightPalette: ThemeColorPalettes['grayLight'] = {
  25: '#FDFDFD',
  50: '#FAFAFA',
  100: '#F5F5F5',
  200: '#E9EAEB',
  300: '#D5D7DA',
  400: '#A4A7AE',
  500: '#717680',
  600: '#535862',
  700: '#414651',
  800: '#252B37',
  900: '#181D27',
  950: '#0A0D12',
};

// Gray Dark Palette
const grayDarkPalette: ThemeColorPalettes['grayDark'] = {
  25: '#FAFAFA',
  50: '#F7F7F7',
  100: '#F0F0F1',
  200: '#ECECED',
  300: '#CECFD2',
  400: '#94979C',
  500: '#85888E',
  600: '#61656C',
  700: '#373A41',
  800: '#22262F',
  900: '#13161B',
  950: '#0C0E12',
};

// Brand Palette
const brandPalette: ThemeColorPalettes['brand'] = {
  25: '#FCFAFF',
  50: '#F9F5FF',
  100: '#F4EBFF',
  200: '#E9D7FE',
  300: '#D6BBFB',
  400: '#B692F6',
  500: '#9E77ED',
  600: '#7F56D9',
  700: '#6941C6',
  800: '#53389E',
  900: '#42307D',
  950: '#2C1C5F',
};

// Error Palette
const errorPalette: ThemeColorPalettes['error'] = {
  25: '#FFFBFA',
  50: '#FEF3F2',
  100: '#FEE4E2',
  200: '#FECDCA',
  300: '#FDA29B',
  400: '#F97066',
  500: '#F04438',
  600: '#D92D20',
  700: '#B42318',
  800: '#912018',
  900: '#7A271A',
  950: '#55160C',
};

// Warning Palette
const warningPalette: ThemeColorPalettes['warning'] = {
  25: '#FFFCF5',
  50: '#FFFAEB',
  100: '#FEF0C7',
  200: '#FEDF89',
  300: '#FEC84B',
  400: '#FDB022',
  500: '#F79009',
  600: '#DC6803',
  700: '#B54708',
  800: '#93370D',
  900: '#7A2E0E',
  950: '#4E1D09',
};

// Success Palette
const successPalette: ThemeColorPalettes['success'] = {
  25: '#F6FEF9',
  50: '#ECFDF3',
  100: '#DCFAE6',
  200: '#ABEFC6',
  300: '#75E0A7',
  400: '#47CD89',
  500: '#17B26A',
  600: '#079455',
  700: '#067647',
  800: '#085D3A',
  900: '#074D31',
  950: '#053321',
};

// Gray Blue Palette
const grayBluePalette: ThemeColorPalettes['grayBlue'] = {
  25: '#FCFCFD',
  50: '#F8F9FC',
  100: '#EAECF5',
  200: '#D5D9EB',
  300: '#B3B8DB',
  400: '#717BBC',
  500: '#4E5BA6',
  600: '#3E4784',
  700: '#363F72',
  800: '#293056',
  900: '#101323',
  950: '#0D0F1C',
};

// Gray Cool Palette
const grayCoolPalette: ThemeColorPalettes['grayCool'] = {
  25: '#FCFCFD',
  50: '#F9F9FB',
  100: '#EFF1F5',
  200: '#DCDFEA',
  300: '#B9C0D4',
  400: '#7D89B0',
  500: '#5D6B98',
  600: '#4A5578',
  700: '#404968',
  800: '#30374F',
  900: '#111322',
  950: '#0E101B',
};

// Gray Modern Palette
const grayModernPalette: ThemeColorPalettes['grayModern'] = {
  25: '#FCFCFD',
  50: '#F8FAFC',
  100: '#EEF2F6',
  200: '#E3E8EF',
  300: '#CDD5DF',
  400: '#9AA4B2',
  500: '#697586',
  600: '#4B5565',
  700: '#364152',
  800: '#202939',
  900: '#121926',
  950: '#0D121C',
};

// Gray Neutral Palette
const grayNeutralPalette: ThemeColorPalettes['grayNeutral'] = {
  25: '#FCFCFD',
  50: '#F9FAFB',
  100: '#F3F4F6',
  200: '#E5E7EB',
  300: '#D2D6DB',
  400: '#9DA4AE',
  500: '#6C737F',
  600: '#4D5761',
  700: '#384250',
  800: '#1F2A37',
  900: '#111927',
  950: '#0D121C',
};

// Gray Iron Palette
const grayIronPalette: ThemeColorPalettes['grayIron'] = {
  25: '#FCFCFC',
  50: '#FAFAFA',
  100: '#F4F4F5',
  200: '#E4E4E7',
  300: '#D1D1D6',
  400: '#A0A0AB',
  500: '#70707B',
  600: '#51525C',
  700: '#3F3F46',
  800: '#26272B',
  900: '#1A1A1E',
  950: '#131316',
};

// Gray True Palette
const grayTruePalette: ThemeColorPalettes['grayTrue'] = {
  25: '#FCFCFC',
  50: '#F7F7F7',
  100: '#F5F5F5',
  200: '#E5E5E5',
  300: '#D6D6D6',
  400: '#A3A3A3',
  500: '#737373',
  600: '#525252',
  700: '#424242',
  800: '#292929',
  900: '#141414',
  950: '#0F0F0F',
};

// Gray Warm Palette
const grayWarmPalette: ThemeColorPalettes['grayWarm'] = {
  25: '#FDFDFC',
  50: '#FAFAF9',
  100: '#F5F5F4',
  200: '#E7E5E4',
  300: '#D7D3D0',
  400: '#A9A29D',
  500: '#79716B',
  600: '#57534E',
  700: '#44403C',
  800: '#292524',
  900: '#1C1917',
  950: '#171412',
};

// Moss Palette
const mossPalette: ThemeColorPalettes['moss'] = {
  25: '#FAFDF7',
  50: '#F5FBEE',
  100: '#E6F4D7',
  200: '#CEEAB0',
  300: '#ACDC79',
  400: '#86CB3C',
  500: '#669F2A',
  600: '#4F7A21',
  700: '#3F621A',
  800: '#335015',
  900: '#2B4212',
  950: '#1A280B',
};

// Green Light Palette
const greenLightPalette: ThemeColorPalettes['greenLight'] = {
  25: '#FAFEF5',
  50: '#F3FEE7',
  100: '#E4FBCC',
  200: '#D0F8AB',
  300: '#A6EF67',
  400: '#85E13A',
  500: '#66C61C',
  600: '#4CA30D',
  700: '#3B7C0F',
  800: '#326212',
  900: '#2B5314',
  950: '#15290A',
};

// Green Palette
const greenPalette: ThemeColorPalettes['green'] = {
  25: '#F6FEF9',
  50: '#EDFCF2',
  100: '#D3F8DF',
  200: '#AAF0C4',
  300: '#73E2A3',
  400: '#3CCB7F',
  500: '#16B364',
  600: '#099250',
  700: '#087443',
  800: '#095C37',
  900: '#084C2E',
  950: '#052E1C',
};

// Teal Palette
const tealPalette: ThemeColorPalettes['teal'] = {
  25: '#F6FEFC',
  50: '#F0FDF9',
  100: '#CCFBEF',
  200: '#99F6E0',
  300: '#5FE9D0',
  400: '#2ED3B7',
  500: '#15B79E',
  600: '#0E9384',
  700: '#107569',
  800: '#125D56',
  900: '#134E48',
  950: '#0A2926',
};

// Cyan Palette
const cyanPalette: ThemeColorPalettes['cyan'] = {
  25: '#F5FEFF',
  50: '#ECFDFF',
  100: '#CFF9FE',
  200: '#A5F0FC',
  300: '#67E3F9',
  400: '#22CCEE',
  500: '#06AED4',
  600: '#088AB2',
  700: '#0E7090',
  800: '#155B75',
  900: '#164C63',
  950: '#0D2D3A',
};

// BlueLight Palette
const blueLightPalette: ThemeColorPalettes['blueLight'] = {
  25: '#F5FBFF',
  50: '#F0F9FF',
  100: '#E0F2FE',
  200: '#B9E6FE',
  300: '#7CD4FD',
  400: '#36BFFA',
  500: '#0BA5EC',
  600: '#0086C9',
  700: '#026AA2',
  800: '#065986',
  900: '#0B4A6F',
  950: '#062C41',
};

// Blue Palette
const bluePalette: ThemeColorPalettes['blue'] = {
  25: '#F5FAFF',
  50: '#EFF8FF',
  100: '#D1E9FF',
  200: '#B2DDFF',
  300: '#84CAFF',
  400: '#53B1FD',
  500: '#2E90FA',
  600: '#1570EF',
  700: '#175CD3',
  800: '#1849A9',
  900: '#194185',
  950: '#102A56',
};

// Blue Dark Palette
const blueDarkPalette: ThemeColorPalettes['blueDark'] = {
  25: '#F5F8FF',
  50: '#EFF4FF',
  100: '#D1E0FF',
  200: '#B2CCFF',
  300: '#84ADFF',
  400: '#528BFF',
  500: '#2970FF',
  600: '#155EEF',
  700: '#004EEB',
  800: '#0040C1',
  900: '#00359E',
  950: '#002266',
};

// Indigo Palette
const indigoPalette: ThemeColorPalettes['indigo'] = {
  25: '#F5F8FF',
  50: '#EEF4FF',
  100: '#E0EAFF',
  200: '#C7D7FE',
  300: '#A4BCFD',
  400: '#8098F9',
  500: '#6172F3',
  600: '#444CE7',
  700: '#3538CD',
  800: '#2D31A6',
  900: '#2D3282',
  950: '#1F235B',
};

// Violet Palette
const violetPalette: ThemeColorPalettes['violet'] = {
  25: '#FBFAFF',
  50: '#F5F3FF',
  100: '#ECE9FE',
  200: '#DDD6FE',
  300: '#C3B5FD',
  400: '#A48AFB',
  500: '#875BF7',
  600: '#7839EE',
  700: '#6927DA',
  800: '#5720B7',
  900: '#491C96',
  950: '#2E125E',
};

// Purple Palette
const purplePalette: ThemeColorPalettes['purple'] = {
  25: '#FAFAFF',
  50: '#F4F3FF',
  100: '#EBE9FE',
  200: '#D9D6FE',
  300: '#BDB4FE',
  400: '#9B8AFB',
  500: '#7A5AF8',
  600: '#6938EF',
  700: '#5925DC',
  800: '#4A1FB8',
  900: '#3E1C96',
  950: '#27115F',
};

// Fuchsia Palette
const fuchsiaPalette: ThemeColorPalettes['fuchsia'] = {
  25: '#FEFAFF',
  50: '#FDF4FF',
  100: '#FBE8FF',
  200: '#F6D0FE',
  300: '#EEAAFD',
  400: '#E478FA',
  500: '#D444F1',
  600: '#BA24D5',
  700: '#9F1AB1',
  800: '#821890',
  900: '#6F1877',
  950: '#47104C',
};

// Pink Palette
const pinkPalette: ThemeColorPalettes['pink'] = {
  25: '#FEF6FB',
  50: '#FDF2FA',
  100: '#FCE7F6',
  200: '#FCCEEE',
  300: '#FAA7E0',
  400: '#F670C7',
  500: '#EE46BC',
  600: '#DD2590',
  700: '#C11574',
  800: '#9E165F',
  900: '#851651',
  950: '#4E0D30',
};

// Rose Palette
const rosePalette: ThemeColorPalettes['rose'] = {
  25: '#FFF5F6',
  50: '#FFF1F3',
  100: '#FFE4E8',
  200: '#FECDD6',
  300: '#FEA3B4',
  400: '#FD6F8E',
  500: '#F63D68',
  600: '#E31B54',
  700: '#C01048',
  800: '#A11043',
  900: '#89123E',
  950: '#510B24',
};

// Orange Dark Palette
const OrangeDarkPalette: ThemeColorPalettes['orangeDark'] = {
  25: '#FFF9F5',
  50: '#FFF4ED',
  100: '#FFE6D5',
  200: '#FFD6AE',
  300: '#FF9C66',
  400: '#FF692E',
  500: '#FF4405',
  600: '#E62E05',
  700: '#BC1B06',
  800: '#97180C',
  900: '#771A0D',
  950: '#57130A',
};

// Orange Palette
const orangePalette: ThemeColorPalettes['orange'] = {
  25: '#FEFAF5',
  50: '#FEF6EE',
  100: '#FDEAD7',
  200: '#F9DBAF',
  300: '#F7B27A',
  400: '#F38744',
  500: '#EF6820',
  600: '#E04F16',
  700: '#B93815',
  800: '#932F19',
  900: '#772917',
  950: '#511C10',
};

// Yellow Palette
const yellowPalette: ThemeColorPalettes['yellow'] = {
  25: '#FEFDF0',
  50: '#FEFBE8',
  100: '#FEF7C3',
  200: '#FEEE95',
  300: '#FDE272',
  400: '#FAC515',
  500: '#EAAA08',
  600: '#CA8504',
  700: '#A15C07',
  800: '#854A0E',
  900: '#713B12',
  950: '#542C0D',
};

// Moss Palette (same as success/green)

// Tertiary Palette (using grayNeutral as base)
const tertiaryPalette: ThemeColorPalettes['tertiary'] = {
  25: '#FCFCFD',
  50: '#F9FAFB',
  100: '#F3F4F6',
  200: '#E5E7EB',
  300: '#D2D6DB',
  400: '#9DA4AE',
  500: '#6C737F',
  600: '#4D5761',
  700: '#384250',
  800: '#1F2A37',
  900: '#111927',
  950: '#0D121C',
};

// Complete color palettes object
const colorPalettes: ThemeColorPalettes = {
  ...baseColors,
  grayLight: grayLightPalette,
  grayDark: grayDarkPalette,
  brand: brandPalette,
  error: errorPalette,
  warning: warningPalette,
  success: successPalette,
  grayBlue: grayBluePalette,
  grayCool: grayCoolPalette,
  grayModern: grayModernPalette,
  grayNeutral: grayNeutralPalette,
  grayIron: grayIronPalette,
  grayTrue: grayTruePalette,
  grayWarm: grayWarmPalette,
  moss: mossPalette,
  greenLight: greenLightPalette,
  green: greenPalette,
  teal: tealPalette,
  cyan: cyanPalette,
  blueLight: blueLightPalette,
  blue: bluePalette,
  blueDark: blueDarkPalette,
  indigo: indigoPalette,
  violet: violetPalette,
  purple: purplePalette,
  fuchsia: fuchsiaPalette,
  pink: pinkPalette,
  rose: rosePalette,
  orangeDark: OrangeDarkPalette,
  orange: orangePalette,
  yellow: yellowPalette,
  tertiary: tertiaryPalette,
};

// Light mode semantic colors
const lightColors = {
  palettes: colorPalettes,
  brand: brandPalette,
  primary: brandPalette[500],
  secondary: bluePalette[500],
  background: baseColors.white,
  surface: grayLightPalette[25],
  text: {
    primary: grayLightPalette[900],
    secondary: grayLightPalette[600],
    disabled: grayLightPalette[400],
  },
  border: grayLightPalette[200],
  error: errorPalette[500],
  warning: warningPalette[500],
  success: successPalette[500],
  info: bluePalette[500],
};

// Dark mode semantic colors
const darkColors = {
  palettes: colorPalettes,
  brand: brandPalette,
  primary: bluePalette[400],
  secondary: brandPalette[400],
  background: grayDarkPalette[950],
  surface: grayDarkPalette[800],
  text: {
    primary: grayDarkPalette[50],
    secondary: grayDarkPalette[300],
    disabled: grayDarkPalette[500],
  },
  border: grayDarkPalette[700],
  error: errorPalette[500],
  warning: warningPalette[500],
  success: successPalette[500],
  info: bluePalette[500],
};

const typography = {
  fontFamily: {
    primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    secondary: '"JetBrains Mono", "Fira Code", monospace',
  },
  fontWeight: {
    light: 300,
    normal: 400,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  display: {
    '2xl': {
      fontSize: { px: '72px', rem: '4.5rem' },
      lineHeight: { px: '90px', rem: '5.625rem' },
      letterSpacing: '-2%',
      fontWeight: 'bold',
    },
    xl: {
      fontSize: { px: '60px', rem: '3.75rem' },
      lineHeight: { px: '72px', rem: '4.5rem' },
      letterSpacing: '-2%',
      fontWeight: 'bold',
    },
    lg: {
      fontSize: { px: '48px', rem: '3rem' },
      lineHeight: { px: '60px', rem: '3.75rem' },
      letterSpacing: '-2%',
      fontWeight: 'bold',
    },
    md: {
      fontSize: { px: '36px', rem: '2.25rem' },
      lineHeight: { px: '44px', rem: '2.75rem' },
      letterSpacing: '-2%',
      fontWeight: 'bold',
    },
    sm: {
      fontSize: { px: '30px', rem: '1.875rem' },
      lineHeight: { px: '38px', rem: '2.375rem' },
      letterSpacing: '-2%',
      fontWeight: 'bold',
    },
    xs: {
      fontSize: { px: '24px', rem: '1.5rem' },
      lineHeight: { px: '32px', rem: '2rem' },
      letterSpacing: '-2%',
      fontWeight: 'bold',
    },
  },
  text: {
    xl: {
      fontSize: { px: '20px', rem: '1.25rem' },
      lineHeight: { px: '30px', rem: '1.875rem' },
      fontWeight: 'regular',
    },
    lg: {
      fontSize: { px: '18px', rem: '1.125rem' },
      lineHeight: { px: '28px', rem: '1.75rem' },
      fontWeight: 'semibold', // Based on design system image
    },
    md: {
      fontSize: {
        sm: { px: '14px', rem: '0.875rem' }, // Mobile: 14px
        md: { px: '16px', rem: '1rem' }, // Desktop/Tablet: 16px
      },
      lineHeight: {
        sm: { px: '20px', rem: '1.25rem' }, // Mobile line height
        md: { px: '24px', rem: '1.5rem' }, // Desktop/Tablet line height
      },
      fontWeight: 'regular',
    },
    sm: {
      fontSize: {
        sm: { px: '12px', rem: '0.75rem' }, // Mobile: 12px
        md: { px: '14px', rem: '0.875rem' }, // Desktop/Tablet: 14px
      },
      lineHeight: {
        sm: { px: '18px', rem: '1.125rem' }, // Mobile line height
        md: { px: '20px', rem: '1.25rem' }, // Desktop/Tablet line height
      },
      fontWeight: 'regular',
    },
    xs: {
      fontSize: { px: '12px', rem: '0.75rem' },
      lineHeight: { px: '18px', rem: '1.125rem' },
      fontWeight: 'regular',
    },
  },
};

const spacing = {
  // Based on design system: 1 unit = 0.25rem (4px)
  0: '0rem', // 0px
  0.5: '0.125rem', // 2px
  1: '0.25rem', // 4px
  1.5: '0.375rem', // 6px
  2: '0.5rem', // 8px
  3: '0.75rem', // 12px
  4: '1rem', // 16px
  5: '1.25rem', // 20px
  6: '1.5rem', // 24px
  8: '2rem', // 32px
  10: '2.5rem', // 40px
  12: '3rem', // 48px
  16: '4rem', // 64px
  20: '5rem', // 80px
  24: '6rem', // 96px
  32: '8rem', // 128px
  40: '10rem', // 160px
  48: '12rem', // 192px
  56: '14rem', // 224px
  64: '16rem', // 256px
  80: '20rem', // 320px
  96: '24rem', // 384px
  120: '30rem', // 480px
  140: '35rem', // 560px
  160: '40rem', // 640px
  180: '45rem', // 720px
  192: '48rem', // 768px
  256: '64rem', // 1024px
  320: '80rem', // 1280px
  360: '90rem', // 1440px
  400: '100rem', // 1600px
  480: '120rem', // 1920px

  // Legacy aliases for backward compatibility
  xs: '0.25rem', // 4px
  sm: '0.5rem', // 8px
  md: '1rem', // 16px
  lg: '1.5rem', // 24px
  xl: '2rem', // 32px
  '2xl': '3rem', // 48px
  '3xl': '4rem', // 64px
};

const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1440px',
  '2xl': '1536px',
};

const lightShadows = {
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  '3xl': '0 35px 60px -12px rgba(0, 0, 0, 0.3)',
};

const darkShadows = {
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.3)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
  '3xl': '0 35px 60px -12px rgba(0, 0, 0, 0.6)',
};

const radius = {
  none: '0rem', // 0px
  xxs: '0.125rem', // 2px
  xs: '0.25rem', // 4px
  sm: '0.375rem', // 6px
  md: '0.5rem', // 8px
  lg: '0.625rem', // 10px
  xl: '0.75rem', // 12px
  '2xl': '1rem', // 16px
  '3xl': '1.25rem', // 20px
  '4xl': '1.5rem', // 24px
  full: '9999px', // Fully rounded (pill shape)
};

const button = {
  borderRadius: {
    sm: radius.sm,
    md: radius.md,
    lg: radius.lg,
  },
  padding: {
    sm: {
      horizontal: '0.75rem',
      vertical: '0.5rem',
    },
    md: {
      horizontal: '1rem',
      vertical: '0.5rem',
    },
    lg: {
      horizontal: '1.5rem',
      vertical: '0.75rem',
    },
  },
  fontSize: {
    sm: '0.875rem', // 14px
    md: '0.875rem', // 14px
    lg: '0.875rem', // 14px
  },
  fontWeight: 500,
  minHeight: {
    sm: '2rem',
    md: '2.5rem',
    lg: '3rem',
  },
  iconSize: {
    sm: 16,
    md: 20,
    lg: 24,
  },
  transition: 'all 0.2s ease-in-out',
  boxShadow: {
    default: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    hover: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    active: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  },
};

export const createTheme = (mode: ThemeMode): Theme => {
  const colors = mode === 'light' ? lightColors : darkColors;
  const shadows = mode === 'light' ? lightShadows : darkShadows;

  return {
    mode,
    colors,
    typography,
    spacing,
    breakpoints,
    shadows,
    radius,
    button,
  };
};

export const lightTheme = createTheme('light');
export const darkTheme = createTheme('dark');
